import { Controller } from "@hotwired/stimulus"

const CLASSI_ATTO = ["testo_bando", "atto_attuativo"]

// Il passo 3 «Classifica i file» di pages/VerificaBandi.js ricalcolava la pagina da `classi` a ogni scelta; qui lo
// stato sono i campi del form e questo controller rifà gli stessi calcoli:
// - scegliendo la classe il tipo d'atto, se vuoto, diventa «circolare» per un atto attuativo e «decreto» altrimenti;
// - il tipo d'atto si sceglie solo per gli atti; la riga dei dati dell'atto c'è per un atto di tipo diverso da «altro»;
// - il menu del trigger della classe è ambra finché la classe manca;
// - «Testo coordinato di» offre gli altri atti che non sono testi coordinati (nome dell'atto o del file), e così il
//   riscontro dell'efficacia; un atto che è l'originale del testo coordinato di un'altra riga lo dice al posto della scelta;
// - l'ordine di prevalenza: gli atti nell'ordine scelto (campo `ordine`), quelli nuovi in fondo; senza atti l'avviso.
// Si ricalcola a ogni input/change e quando il form è riletto con morph. Una riga con la classe e una parte modificata
// resta tutta com'è (tieniRiga).
export default class extends Controller {
  static targets = ["riga", "atto", "mancanti", "ordine", "stato", "lista", "voce"]
  static values = { tipi: Object }

  connect() {
    this.aggiorna()
  }

  // turbo:morph-element arriva anche dai figli: si ricalcola quando il form intero è stato riletto
  riletto(event) {
    if (event.target === this.element) this.aggiorna()
  }

  aggiorna(event) {
    const t = event?.target
    if (t?.dataset?.campo === "classe") this.tipoPredefinito(t)
    this.tieniRiga(t)
    if (t?.matches?.("[data-coordinato-select]")) {
      const campo = t.closest("[data-coordinato-scelta]").querySelector("[data-campo='coordinato_di']")
      campo.value = t.value
    }
    const righe = this.rigaTargets.map((el) => ({
      el, id: el.dataset.docId, nomeFile: el.dataset.nome || "",
      classe: this.campo(el, "classe"), tipo: this.campo(el, "tipo_atto"),
      nomeAtto: this.campo(el, "nome_atto"), coordinato: this.campo(el, "coordinato_di"),
    }))
    righe.forEach((r) => this.riga(r, righe))
    this.ordina(righe)
  }

  // proponi() teneva la scelta intera di una riga con una classe (c[k]?.classe ? c[k] : v): quando una parte di una riga
  // con la classe è modificata, tutte le unità della riga lo diventano, così la scheda riletta non cambia la classe (e
  // non nasconde i dati dell'atto scritti) né le altre parti della riga
  tieniRiga(t) {
    const riga = t?.closest?.("[data-classi-documenti-target='riga']")
    if (!riga || !t.closest("[data-conserva-modifiche-unita]") || !this.campo(riga, "classe")) return
    riga.querySelectorAll("[data-conserva-modifiche-unita]").forEach((u) => { u.dataset.conservaModificheModificata = "" })
  }

  campo(el, nome) {
    return el.querySelector(`[data-campo='${nome}']`)?.value || ""
  }

  tipoPredefinito(campoClasse) {
    const riga = campoClasse.closest("tbody")
    const tipo = riga?.querySelector("[data-campo='tipo_atto']")
    if (!tipo || tipo.value) return
    tipo.value = campoClasse.value === "atto_attuativo" ? "circolare" : "decreto"
    tipo.closest("[data-tipo-atto]")?.dispatchEvent(new CustomEvent("classi-documenti:sincronizza"))
  }

  riga(r, righe) {
    const atto = CLASSI_ATTO.includes(r.classe)
    const trigger = r.el.querySelector(`[data-testid='doc-classe-${CSS.escape(r.id)}']`)
    trigger?.classList.toggle("border-amber-400", !r.classe)
    trigger?.classList.toggle("text-amber-700", !r.classe)
    const tipo = r.el.querySelector("[data-tipo-atto]")
    if (tipo) tipo.hidden = !atto
    const datiAtto = r.el.querySelector("[data-classi-documenti-target='atto']")
    if (datiAtto) datiAtto.hidden = !(atto && r.tipo !== "altro")

    const altri = righe.filter((x) => x.id !== r.id && CLASSI_ATTO.includes(x.classe) && !x.coordinato)
      .map((x) => ({ id: x.id, nome: x.nomeAtto || x.nomeFile }))
    r.el.querySelectorAll("select[data-altri]").forEach((sel) => {
      const valore = sel.matches("[data-coordinato-select]") ? r.coordinato : sel.value
      const opzioni = [new Option(sel.dataset.altri, ""), ...altri.map((a) => new Option(a.nome, a.id))]
      sel.replaceChildren(...opzioni)
      sel.value = altri.some((a) => a.id === valore) ? valore : ""
    })

    const c2 = righe.find((x) => x.id !== r.id && x.coordinato === r.id && CLASSI_ATTO.includes(x.classe))
    const da = c2 && !r.coordinato ? (c2.nomeAtto || c2.nomeFile) : null
    const nota = r.el.querySelector("[data-coordinato-da]")
    if (nota) {
      nota.hidden = !da
      nota.querySelector("[data-nome]").textContent = da || ""
    }
    const scelta = r.el.querySelector("[data-coordinato-scelta]")
    if (scelta) scelta.hidden = !!da
    const spiegazione = r.el.querySelector("[data-coordinato-nota]")
    if (spiegazione) spiegazione.hidden = !r.coordinato
  }

  // attiOrdinati: [...ordine ∩ atti, ...atti nuovi]; le voci degli altri file restano nascoste
  ordina(righe) {
    const atti = righe.filter((r) => CLASSI_ATTO.includes(r.classe) && r.tipo !== "altro")
    const ids = atti.map((r) => r.id)
    if (this.hasMancantiTarget) this.mancantiTarget.hidden = ids.length > 0
    if (this.hasOrdineTarget) this.ordineTarget.hidden = ids.length === 0
    if (!this.hasListaTarget) return
    let ordine = []
    try { ordine = JSON.parse(this.statoTarget.value || "[]") } catch { ordine = [] }
    if (!Array.isArray(ordine)) ordine = []
    const ordinati = [...ordine.filter((x) => ids.includes(x)), ...ids.filter((x) => !ordine.includes(x))]
    const voci = new Map(this.voceTargets.map((v) => [v.dataset.docId, v]))
    const perId = new Map(righe.map((r) => [r.id, r]))
    ordinati.forEach((id, i) => {
      const voce = voci.get(id)
      if (!voce) return
      const r = perId.get(id)
      this.listaTarget.appendChild(voce)
      voce.hidden = false
      voce.dataset.testid = `atto-${i}`
      voce.querySelector("[data-numero]").textContent = `${i + 1}.`
      voce.querySelector("[data-nome]").textContent = r.nomeAtto || r.nomeFile
      voce.querySelector("[data-tipo]").textContent = `(${this.tipiValue[r.tipo] ?? ""})`
      const su = voce.querySelector("[data-su]")
      su.dataset.testid = `atto-su-${i}`
      su.disabled = i === 0
      const giu = voce.querySelector("[data-giu]")
      giu.dataset.testid = `atto-giu-${i}`
      giu.disabled = i === ordinati.length - 1
    })
    voci.forEach((voce, id) => {
      if (ordinati.includes(id)) return
      this.listaTarget.appendChild(voce)
      voce.hidden = true
      delete voce.dataset.testid
      voce.querySelectorAll("button").forEach((b) => { delete b.dataset.testid })
    })
  }
}
